import React, { useCallback, useMemo } from 'react';

import { useCurrentUser } from 'Auth';

import { useQueryClient } from '@gogame/query';

import { useLoadAudience } from 'scenes/CustomAudiences/Audiences/data';
import {
  useCreateAudience,
  useUpdateAudience,
} from 'scenes/CustomAudiences/Audiences/data/mutations';
import { grid } from 'scenes/CustomAudiences/Audiences/data/queryKeyFactory';
import { useBatchCreatePartnerAudience } from 'scenes/CustomAudiences/PartnerAudiences/data/mutations';

import AudienceForm from './AudienceForm';
import * as AUDIENCE_TYPES_FIELDSET from './AudienceTypesFormField/constants';
import { NAME, OWNER } from './GeneralFieldSet/constants';
import { TRAFFIC_TYPE, TRAFFIC_TYPE_ALL } from './PopulationFieldSet/constants';
import {
  AUDIENCE_TYPES,
  getAudienceFromValues,
  getPartnerAudiencesFromValues,
} from './constants';

export const AudienceFormCreate = ({ onSubmit, onClose, onExited, open }) => {
  const [createAudience, { data }] = useCreateAudience({ refresh: false });
  const [batchCreatePartnerAudiences, { isLoading: isCreating }] =
    useBatchCreatePartnerAudience({ refresh: false });
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const handleSubmit = useCallback(
    async (values = {}) => {
      const audience = getAudienceFromValues(values);
      const createdAudience = await createAudience(audience);

      const partnerAudiences = getPartnerAudiencesFromValues(values).map(
        (partnerAudience) => ({
          ...partnerAudience,
          parentAudience: createdAudience.id,
          owner: values[OWNER],
        })
      );

      await batchCreatePartnerAudiences(partnerAudiences);
      await queryClient.resetQueries({
        queryKey: grid(),
      });
      onSubmit(data);
    },
    [data]
  );

  const initialValues = useMemo(
    () => ({
      [OWNER]: user?.id,
      [TRAFFIC_TYPE]: TRAFFIC_TYPE_ALL,
      [AUDIENCE_TYPES]: [
        AUDIENCE_TYPES_FIELDSET.NON_DEPOSITORS,
        AUDIENCE_TYPES_FIELDSET.SUBSCRIBERS,
        AUDIENCE_TYPES_FIELDSET.REGULAR,
      ],
    }),
    [user]
  );

  return (
    <AudienceForm
      title={'Add Audience'}
      loading={isCreating}
      onClose={onClose}
      onExited={onExited}
      open={open}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      isShowPartnerAudiences={true}
    />
  );
};

export const AudienceFormEdit = ({ id, onClose, onSubmit, onExited, open }) => {
  const { data, isLoading } = useLoadAudience({ id });
  const [updateAudience, { isLoading: isUpdating }] = useUpdateAudience();

  const handleSubmit = async (values) => {
    const audience = getAudienceFromValues(values);
    const response = await updateAudience(audience);
    onSubmit(response);
  };

  return (
    <AudienceForm
      title={'Edit Audience'}
      subTitle={data?.[NAME]}
      entityId={id}
      loading={isLoading || isUpdating}
      onClose={onClose}
      onExited={onExited}
      open={open}
      initialValues={data}
      editGranularity={data?.editGranularity}
      active={!!data}
      onSubmit={handleSubmit}
      isShowPartnerAudiences={false}
    />
  );
};

export const AudienceFormDuplicate = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const [createAudience, { isLoading: isCreating }] = useCreateAudience();
  const { data, isLoading } = useLoadAudience({ id });

  const handleSubmit = async (values = {}) => {
    const audience = getAudienceFromValues(values);
    const createdAudience = await createAudience(audience);

    onSubmit(createdAudience);
  };

  return (
    <AudienceForm
      title={'Add Audience'}
      loading={isLoading || isCreating}
      onClose={onClose}
      onExited={onExited}
      open={open}
      initialValues={data}
      active={!!data}
      onSubmit={handleSubmit}
      isShowPartnerAudiences={false}
    />
  );
};
